/* ---------------------------------------------------------------------------
   Cómo se leen las cifras del tablero de consumo de IA (`/admin/ia/consumo`).

   Archivo propio y no una reutilización de `lib/formatoDeMercado.ts` ni de
   `lib/montos.ts`: cada módulo del sistema tiene su propia lectura de números
   porque cada uno resuelve una ambigüedad distinta (acá, "sin precio" contra
   "cero"), y compartir el archivo acoplaría dos dominios que no tienen nada
   que ver — el catálogo de comparables y el gasto en IA.

   UN COSTO `null` NO ES UN COSTO CERO. Es la regla que atraviesa todo el
   archivo: `ia_consumos.costo_usd` es `null` cuando NINGÚN precio estaba
   registrado para ese modelo en el momento de la llamada — "no sabemos cuánto
   costó", no "no costó nada". Mostrarlo como "$0" escondería exactamente la
   brecha que el aviso de "peticiones sin precio" existe para señalar.
--------------------------------------------------------------------------- */

const ES = "es-CL";

/**
 * Un costo en dólares, tal como lo entrega el API: un string `decimal(?,6)` o
 * `null`.
 *
 * LOS DECIMALES SE ADAPTAN A LA MAGNITUD, y no es cosmética: un total del
 * período suele ser un dólar o más y dos decimales alcanzan; el costo de UNA
 * petición suele ser una fracción de centavo, y redondearlo a dos decimales lo
 * mostraría como "US$ 0,00" — un cero que no es el que el dato dice.
 */
export function formatearCostoUsd(valor: string | null): string {
  if (valor === null) return "Sin precio";

  const numero = Number(valor);
  if (!Number.isFinite(numero)) return "Sin precio";

  const decimales = Math.abs(numero) >= 1 ? 2 : 6;

  return `US$ ${numero.toLocaleString(ES, {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimales,
  })}`;
}

/** Un conteo de tokens (o de peticiones), con el separador de miles chileno. */
export function formatearTokens(valor: number): string {
  return valor.toLocaleString(ES);
}

/**
 * Qué proporción del costo total representa una parte, o "—" cuando no se
 * puede calcular.
 *
 * Ninguno de los dos operandos puede ser `null`: sin el total no hay contra
 * qué comparar, y sin la parte esa fila no tiene ni un costo conocido —las dos
 * cosas son "no lo sé", nunca "cero por ciento".
 */
export function formatearPorcentaje(parte: string | null, total: string | null): string {
  if (parte === null || total === null) return "—";

  const numParte = Number(parte);
  const numTotal = Number(total);
  if (!Number.isFinite(numParte) || !Number.isFinite(numTotal) || numTotal === 0) return "—";

  return `${((numParte / numTotal) * 100).toLocaleString(ES, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

/**
 * Una fecha de calendario `"YYYY-MM-DD"` (la que agrupa `por_dia`) leída
 * corta: `"día-mes"`.
 *
 * Igual que `formatearFecha` de `lib/fechas.ts`: se parte el string y nunca se
 * construye un `Date`. No es un instante — es el día que el SERVIDOR agrupó—,
 * así que convertirlo con huso horario lo correría de lugar sin motivo.
 */
export function diaCorto(fechaIso: string): string {
  const [, mes, dia] = fechaIso.slice(0, 10).split("-");
  if (!mes || !dia) return fechaIso;

  return `${dia}-${mes}`;
}
